// The repository-file routes the app shell's path table holds. Kept to the address constants: `shell-bridge.ts` reads
// them at launch, and `contract.ts` (the watch rules and their English words) must stay out of the web entry (T-0770).

import { MAX_NAMESPACE_DEPTH } from '../addressing/paths.js';

/**
 * The file route the app shell's path table holds, once per namespace depth. Its ranking puts the
 * static `-` and `files` above any number of parameters, so the one splat takes every file path.
 */
export const REPOSITORY_FILE_SPLAT_ROUTE = 'files/*';

/**
 * I-1774 (§12.9.2, question 4): a qualified reference's address names its folder,
 * `/<ns>/<wa>/-/folders/<folder>@<device>/files/<path>`. The app shell's path table holds this splat
 * once per namespace depth, as it holds `files/*`.
 */
export const REPOSITORY_FOLDER_FILE_SPLAT_ROUTE = `folders/:folder/${REPOSITORY_FILE_SPLAT_ROUTE}`;

/**
 * How many segments of a file path the work area's file routes name one by one. React Router ranks
 * a splat below a route of parameters only, so `files/*` lost a path of three segments or more to a
 * deeper work-area route (`/:ns1/…/:ns10/:workArea`), whose parameters then spelled
 * `acme/tracker/-/files/docs/…`, and the page said there was no page (found by
 * `repository-file.spec.ts`). The deepest such route is `MAX_NAMESPACE_DEPTH + 1` segments; a file
 * address at the shallowest depth is 4 segments before its path, so a path longer than this cannot
 * be taken by one, and `files/*` serves it.
 */
export const REPOSITORY_FILE_ROUTE_DEPTH = MAX_NAMESPACE_DEPTH - 3;

/**
 * The file routes of a work area in the web router, as its routes are declared inside it:
 * `files/:s1`, `files/:s1/:s2`, … and `files/*`. `pathConfiguration.guard.test.ts` holds the
 * shell table's one splat to cover each of them.
 */
export function repositoryFileRoutes(): string[] {
  const routes: string[] = [];
  for (let depth = 1; depth <= REPOSITORY_FILE_ROUTE_DEPTH; depth += 1) {
    routes.push(`files/${Array.from({ length: depth }, (_, index) => `:s${index + 1}`).join('/')}`);
  }
  routes.push(REPOSITORY_FILE_SPLAT_ROUTE);
  return routes;
}

/**
 * The routes of a file in a named folder, as `repositoryFileRoutes` are for a plain path: one per
 * depth, then a splat, so a deeper work-area route never takes one.
 */
export function repositoryFolderFileRoutes(): string[] {
  return repositoryFileRoutes().map((route) => `folders/:folder/${route}`);
}

/**
 * I-1725 (FG-103): a full commit id, SHA-1 or SHA-256, lowercase — what a ticket may name as the
 * commit to read at. Here, beside the routes, so the file address (`open-address.ts`) reads it without
 * pulling `contract.ts` and its watch rules into the web entry (I-2453).
 */
export const REPOSITORY_FILE_COMMIT_PATTERN = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;
